import { isUUID } from "@/lib/validators/uuid";

/**
 * OBJECT-KEY CONSTRUCTION — the one module that builds a storage path.
 *
 * `.claude/rules/storage.md` §Paths. Every write path in the app is produced
 * here, by a named builder, and the builders are the ONLY way to obtain a
 * `StoragePath`. That is the point: the stores' write operations accept
 * `StoragePath<B>` and nothing else, so a hand-concatenated string is a TYPE
 * ERROR rather than a code-review question.
 *
 * WHY THIS EXISTS. `deal-documents` is shared by five aggregates (deals,
 * offerings, requests, OCR scratch, signed contracts) and its RLS policies key
 * on the object key's FIRST FOLDER:
 *
 *   (storage.foldername(name))[1] = auth.uid()::text     -- INSERT/UPDATE/DELETE
 *
 * so "the path starts with the acting user's id" is a security invariant, not a
 * naming convention. Before this module it was re-derived by hand at six call
 * sites in six shapes. A seventh that forgot the prefix would not have failed a
 * type-check, a test or a review — it would have failed in Supabase, at
 * runtime, as a 403 nobody reads.
 *
 * WHAT ENFORCES WHAT — stated explicitly, per `.claude/rules/verification.md`:
 *  - The owner prefix on a WRITE is enforced by Postgres (the RLS policies
 *    above) whenever the caller uses an RLS-scoped client. `ownerScopedPath`
 *    makes the app produce a path the policy will accept; it does not itself
 *    grant anything.
 *  - Under a SERVICE-ROLE client RLS is bypassed and nothing in the database
 *    checks the prefix. There these builders are the only guard, which is why
 *    `systemScopedPath` is a separate, differently-named entry point: a
 *    service-role write is a deliberate act, never a default.
 *  - Traversal and separator injection are enforced HERE, by `assertSegment`,
 *    on every builder, and pinned by `paths.test.ts`.
 *  - READING a path back out of a database column is a different problem with a
 *    different guard: stored values are plain strings and are scoped by
 *    `resolveDealDocumentUrl`'s REQUIRED `allowedFolderPrefixes` (finding H3).
 *    Branding cannot help there — the value has already left the type system.
 */

declare const StoragePathBrand: unique symbol;

/**
 * A validated, bucket-relative object key. Nominal by construction: the brand
 * is a `unique symbol` declared (never assigned) in this module, so no code
 * outside it can produce a value of this type without a cast.
 */
export type StoragePath<Bucket extends string> = string & {
  readonly [StoragePathBrand]: Bucket;
};

/** Raised for any path that would be unsafe or unusable as an object key. */
export class StoragePathError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "StoragePathError";
  }
}

/**
 * Supabase caps object keys at 1024 bytes; a single segment longer than this is
 * a bug (or an attack) rather than a filename.
 */
const MAX_SEGMENT_LENGTH = 120;
const MAX_PATH_LENGTH = 1024;

/** Control characters and NUL — they truncate or corrupt the key on the wire. */
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;

/**
 * Reject anything that could escape the folder it is meant to sit in, or that
 * storage cannot address. `..` is the traversal case; an embedded `/` is the
 * subtler one — it lets a caller that legitimately supplies ONE segment invent
 * a whole prefix, which is how an owner-scoped path stops being owner-scoped.
 */
function assertSegment(segment: string, label: string): void {
  if (segment.length === 0) {
    throw new StoragePathError(`Empty ${label} in storage path`);
  }
  if (segment.length > MAX_SEGMENT_LENGTH) {
    throw new StoragePathError(`${label} exceeds ${MAX_SEGMENT_LENGTH} characters in storage path`);
  }
  if (segment === "." || segment === "..") {
    throw new StoragePathError(`Relative ${label} in storage path`);
  }
  if (segment.includes("/") || segment.includes("\\")) {
    throw new StoragePathError(`Path separator inside ${label}`);
  }
  if (CONTROL_CHARACTERS.test(segment)) {
    throw new StoragePathError(`Control character inside ${label}`);
  }
}

function assemble<Bucket extends string>(segments: string[]): StoragePath<Bucket> {
  for (const segment of segments) assertSegment(segment, "segment");
  const path = segments.join("/");
  if (path.length > MAX_PATH_LENGTH) {
    throw new StoragePathError(`Storage path exceeds ${MAX_PATH_LENGTH} characters`);
  }
  return path as StoragePath<Bucket>;
}

/**
 * Slug an arbitrary user-supplied file name down to something safe to place in
 * an object key, preserving enough of the original that a signed-URL download
 * still arrives with a recognisable name. Lifted from the attachments service,
 * which is now one of this module's callers rather than a second copy.
 */
export function sanitizeFileName(fileName: string, maxLength: number = MAX_SEGMENT_LENGTH): string {
  const sanitised = fileName
    .normalize("NFKD")
    .replace(/[^\w.\-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, maxLength);
  return sanitised || "file";
}

/**
 * The segment budget a caller has left after prepending `prefix` to a
 * sanitised name.
 *
 * EXISTS BECAUSE THE OBVIOUS VERSION IS WRONG. `sanitizeFileName` used to slice
 * to the WHOLE 120-char segment budget, and `entityAttachmentPath` then glued a
 * 37-char `<uuid>-` in front of it — producing segments up to 157 chars that
 * `assertSegment` rejects. Every attachment with a sanitised name of 84+ chars
 * threw instead of uploading, as a bare 500 (`StoragePathError` is not an
 * `AttachmentServiceError`). 8 of 178 rows already in the production bucket have
 * a final segment longer than 120 chars, so this was a live regression, not a
 * theoretical one. Budgets do not compose by accident: subtract the prefix.
 */
export function remainingSegmentBudget(prefix: string): number {
  return Math.max(1, MAX_SEGMENT_LENGTH - prefix.length);
}

/**
 * `<ownerId>/<...segments>` — the shape every RLS-gated bucket policy expects.
 *
 * `ownerId` MUST be a uuid: the policy compares folder 1 against
 * `auth.uid()::text`, so a non-uuid owner produces a path that can never be
 * written and, worse, reads as intentional at the call site. Rejecting it here
 * turns a silent 403 into a loud error at the point of the mistake.
 */
export function ownerScopedPath<Bucket extends string>(
  ownerId: string,
  ...segments: string[]
): StoragePath<Bucket> {
  if (!isUUID(ownerId)) {
    throw new StoragePathError("Owner-scoped storage path requires a uuid owner id");
  }
  return assemble<Bucket>([ownerId, ...segments]);
}

/**
 * The reserved first folder for objects written by the SYSTEM under a
 * service-role client, where there is no `auth.uid()` to scope to (the signed
 * contract DocuSign hands back, for instance).
 *
 * `admin` is not a uuid, so `(storage.foldername(name))[1] = auth.uid()::text`
 * can never be true for these objects: no end user can read, overwrite or
 * delete one under RLS, and the only SELECT that reaches them is the
 * `is_platform_admin()` branch of the deal-documents read policy. That is a
 * property of the value, not of a policy we have to keep in sync.
 */
export const SYSTEM_PATH_PREFIX = "admin";

export function systemScopedPath<Bucket extends string>(
  ...segments: string[]
): StoragePath<Bucket> {
  return assemble<Bucket>([SYSTEM_PATH_PREFIX, ...segments]);
}

/**
 * `<entityType>/<entityId>/<...segments>` — the attachments bucket scopes by
 * parent ENTITY rather than by user, because its read policy is row-driven (an
 * owner may read objects whose path appears in an `attachments` row for an
 * entity they own). `AttachmentService.confirmUpload` re-checks this same
 * prefix against the client-supplied path; see the forgery-guard test.
 */
export function entityScopedPath<Bucket extends string>(
  entityType: string,
  entityId: string,
  ...segments: string[]
): StoragePath<Bucket> {
  // DELIBERATELY NOT uuid-checked, unlike `ownerScopedPath`. The asymmetry is
  // the point: the owner check exists because the deal-documents policy
  // compares folder 1 against `auth.uid()::text`, so a non-uuid there produces
  // a path no policy can ever accept. The attachments read policy parses NO
  // part of the path — it joins `attachments.entity_id` to the entity table —
  // so the path's shape carries no security meaning here and a uuid assertion
  // would be a guard borrowed from a mechanism that is not in play. (The
  // column is `uuid`, so a bad id is rejected by the INSERT regardless.)
  return assemble<Bucket>([entityType, entityId, ...segments]);
}

/**
 * The prefix an owner-scoped path is required to start with. The single place
 * the `${id}/` spelling lives, so a caller cannot get the trailing slash wrong
 * and turn `user-1/` into a prefix that also matches `user-10/`.
 */
export function ownerFolderPrefix(ownerId: string): string {
  return `${ownerId}/`;
}

/**
 * `<entityType>/<entityId>/` — the entity equivalent of `ownerFolderPrefix`,
 * and the exact string `confirmUpload` pins a client-supplied path against.
 */
export function entityFolderPrefix(entityType: string, entityId: string): string {
  return `${entityType}/${entityId}/`;
}

/**
 * Build the file-name segment for a timestamped upload: `<epochMs>.<ext>`.
 *
 * The extension is taken from an ALLOW-LIST rather than from the uploaded name,
 * so a crafted `file.pdf.html` cannot land an HTML extension in a bucket whose
 * objects are served from a Supabase origin. Callers that already validated the
 * MIME type still pass through here, because the two checks answer different
 * questions: MIME is what the client CLAIMS, the extension is what the object
 * will be served AS.
 */
export function timestampedFileName(
  originalName: string,
  allowedExtensions: string[],
  fallbackExtension: string
): string {
  const raw = originalName.split(".").pop()?.toLowerCase() ?? "";
  const ext = allowedExtensions.includes(raw) ? raw : fallbackExtension;
  return `${Date.now()}.${ext}`;
}
